import { Fragment } from 'react';

/**
 * "Watch your money stay put" — the protected-payment story as a live visual.
 * Buyer view (checkout/payment) and seller view (payout) share one component.
 * Fully theme-aware: it reads the app's --c-* CSS variables, so it flips with
 * Day / Night Souq automatically.
 */
type Variant = 'buyer' | 'seller';

const STEPS: Record<Variant, { t: string; s: string; k: 'card' | 'vault' | 'check' }[]> = {
  buyer: [
    { t: 'You pay', s: 'Card charged, full price shown', k: 'card' },
    { t: 'Held by Souqli', s: 'Safe — not the seller’s yet', k: 'vault' },
    { t: 'You confirm → seller paid', s: 'Only then does it release', k: 'check' },
  ],
  seller: [
    { t: 'Buyer pays', s: 'Your sale is funded upfront', k: 'card' },
    { t: 'Held by Souqli', s: 'Safe until the buyer confirms', k: 'vault' },
    { t: 'You’re paid', s: 'Released to your wallet instantly', k: 'check' },
  ],
};

function Glyph({ k }: { k: 'card' | 'vault' | 'check' }) {
  const p = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (k === 'card') return (<svg {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>);
  if (k === 'vault') return (<svg {...p}><path d="M12 3 5 6v6c0 4 3 6.6 7 8 4-1.4 7-4 7-8V6l-7-3Z" /><rect x="9.2" y="11" width="5.6" height="4.6" rx="1" /><path d="M10.4 11V9.6a1.6 1.6 0 0 1 3.2 0V11" /></svg>);
  return (<svg {...p}><path d="M20 6 9 17l-5-5" /></svg>);
}

export function MoneyLedger({ variant = 'buyer', className = '' }: { variant?: Variant; className?: string }) {
  const steps = STEPS[variant];
  const blurb = variant === 'seller'
    ? 'Your sale is funded before you ship. The money is held safely and lands in your wallet the moment the buyer confirms — no chasing payment.'
    : 'Your payment is held by Souqli — through Stripe — and only released to the seller once you confirm the item arrived as described. If it doesn’t, the money comes back to you.';
  return (
    <div className={`souqli-ledger ${className}`}>
      <style>{`
        .souqli-ledger{border:1px solid rgb(var(--c-warmline));background:rgb(var(--c-cream-deep));border-radius:16px;padding:20px}
        .souqli-ledger .sl-head{display:flex;align-items:center;gap:8px;margin-bottom:6px;color:rgb(var(--c-gold-dark))}
        .souqli-ledger .sl-head span{font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;font-weight:700}
        .souqli-ledger .sl-blurb{font-size:12.5px;line-height:1.55;color:rgb(var(--c-mute));max-width:56ch;margin-bottom:20px}
        .souqli-ledger .sl-flow{display:flex;flex-direction:column;gap:10px}
        @media(min-width:640px){.souqli-ledger .sl-flow{flex-direction:row;align-items:stretch}}
        .souqli-ledger .sl-node{flex:1;background:rgb(var(--c-surface));border:1px solid rgb(var(--c-warmline));border-radius:13px;padding:14px;text-align:center}
        .souqli-ledger .sl-ic{width:40px;height:40px;border-radius:11px;margin:0 auto 9px;display:grid;place-items:center;background:rgb(var(--c-cream-deep));color:rgb(var(--c-mute));animation:sl-ic 6s infinite}
        .souqli-ledger .sl-node:nth-child(1) .sl-ic{animation-delay:0s}
        .souqli-ledger .sl-node:nth-child(3) .sl-ic{animation-delay:-2s}
        .souqli-ledger .sl-node:nth-child(5) .sl-ic{animation-delay:-4s}
        @keyframes sl-ic{0%,20%{background:rgb(var(--c-gold));color:#fff;box-shadow:0 0 0 4px rgb(var(--c-gold) / .22)}21%,100%{background:rgb(var(--c-cream-deep));color:rgb(var(--c-mute));box-shadow:none}}
        .souqli-ledger .sl-node h4{font-size:13px;font-weight:700;color:rgb(var(--c-ink));margin-bottom:2px;line-height:1.2}
        .souqli-ledger .sl-node p{font-size:11px;color:rgb(var(--c-mute));line-height:1.35}
        .souqli-ledger .sl-track{position:relative;background:rgb(var(--c-warmline));border-radius:2px}
        @media(min-width:640px){.souqli-ledger .sl-track{width:34px;height:3px;align-self:center;margin:0 4px}}
        @media(max-width:639px){.souqli-ledger .sl-track{width:3px;height:16px;margin:0 auto}.souqli-ledger .sl-dot{display:none}}
        .souqli-ledger .sl-dot{position:absolute;top:50%;left:0;width:9px;height:9px;border-radius:50%;background:rgb(var(--c-gold));transform:translate(-50%,-50%);box-shadow:0 0 0 3px rgb(var(--c-gold) / .3)}
        .souqli-ledger .sl-track:nth-child(2) .sl-dot{animation:sl-run 6s ease-in-out infinite}
        .souqli-ledger .sl-track:nth-child(4) .sl-dot{animation:sl-run 6s ease-in-out infinite;animation-delay:-3s}
        @keyframes sl-run{0%{left:0;opacity:0}8%{opacity:1}45%{left:100%}50%,100%{left:100%;opacity:0}}
        @media(prefers-reduced-motion:reduce){.souqli-ledger *{animation:none!important}.souqli-ledger .sl-node:nth-child(3) .sl-ic{background:rgb(var(--c-gold));color:#fff}}
      `}</style>
      <div className="sl-head">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 5 6v6c0 4 3 6.6 7 8 4-1.4 7-4 7-8V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></svg>
        <span>Watch your money stay put</span>
      </div>
      <p className="sl-blurb">{blurb}</p>
      <div className="sl-flow">
        {steps.map((st, i) => (
          <Fragment key={st.t}>
            <div className="sl-node">
              <div className="sl-ic"><Glyph k={st.k} /></div>
              <h4>{st.t}</h4>
              <p>{st.s}</p>
            </div>
            {i < steps.length - 1 && <div className="sl-track"><div className="sl-dot" /></div>}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
